import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, X, Plus, Minus, Trash2, Loader2 } from "lucide-react";
import { useCart } from "@/hooks/useCart";
import { toast } from "sonner";
import axios from "axios";

import { API } from "@/lib/api";

export default function CartDrawer() {
  const { isOpen, closeCart, detailed, total, count, removeItem, setQuantity, clear } = useCart();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const navigate = useNavigate();

  const onCheckout = async () => {
    if (!detailed.length) {
      toast.error("Koszyk jest pusty");
      return;
    }
    if (email && !email.match(/.+@.+\..+/)) {
      toast.error("Sprawdź adres e-mail");
      return;
    }
    setLoading(true);
    try {
      const items = detailed.map((i) => ({
        lookup_key: i.product.lookup_key,
        quantity: i.quantity,
      }));
      const res = await axios.post(`${API}/payments/checkout`, {
        items,
        origin_url: window.location.origin,
        email: email || undefined,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Nie udało się otworzyć płatności. Spróbuj ponownie.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-[#051937]/40 backdrop-blur-sm z-[70]"
            onClick={closeCart}
            data-testid="cart-backdrop"
          />
          <motion.aside
            key="drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-[80] flex flex-col shadow-[0_-40px_100px_rgba(11,42,85,0.4)]"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-6 border-b border-[#BAE6FD]">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-[#84C5F4] grid place-items-center text-white">
                  <ShoppingBag size={18} />
                </span>
                <div>
                  <h3 className="font-display font-semibold text-[#051937] text-xl leading-none">
                    Twój koszyk
                  </h3>
                  <p className="text-xs uppercase tracking-[0.16em] font-bold text-[#0b2a55]/60 mt-1">
                    {count} {count === 1 ? "pozycja" : "pozycje"}
                  </p>
                </div>
              </div>
              <button
                onClick={closeCart}
                data-testid="cart-close"
                className="w-10 h-10 rounded-full bg-[#F4FAFF] hover:bg-[#E0F2FE] grid place-items-center text-[#051937]"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {detailed.length === 0 && (
                <div className="text-center py-16" data-testid="cart-empty">
                  <div className="text-6xl mb-4">👻</div>
                  <p className="font-display font-semibold text-[#051937] text-xl">Duszek zajrzał — koszyk pusty.</p>
                  <p className="mt-3 text-[#0b2a55]/70">Dodaj książkę lub kolorowankę.</p>
                  <Link
                    to="/sklep"
                    onClick={closeCart}
                    className="inline-flex items-center gap-2 mt-6 rounded-full bg-[#051937] text-white px-5 py-3 font-semibold text-sm"
                    data-testid="cart-goto-shop"
                  >
                    Do sklepu
                  </Link>
                </div>
              )}

              {detailed.map((i) => (
                <div
                  key={i.lookup_key}
                  className="flex gap-4 rounded-2xl border border-[#BAE6FD]/60 p-3"
                  data-testid={`cart-item-${i.product.slug}`}
                >
                  <div
                    className="w-16 h-20 rounded-xl overflow-hidden shrink-0"
                    style={{ background: `linear-gradient(160deg, ${i.product.color} 0%, ${i.product.accent}55 100%)` }}
                  >
                    <img
                      src={`/assets/portrait-${i.product.cover_char || "duszek"}.png`}
                      alt=""
                      className="w-full h-full object-contain p-1"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-[#051937] text-sm leading-tight truncate">
                      {i.product.title}
                    </div>
                    <div className="text-xs text-[#0b2a55]/60 mt-1">
                      {i.product.price_pln} zł / szt
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        onClick={() => setQuantity(i.lookup_key, i.quantity - 1)}
                        data-testid={`cart-minus-${i.product.slug}`}
                        className="w-7 h-7 rounded-full bg-[#F4FAFF] hover:bg-[#E0F2FE] grid place-items-center text-[#051937]"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="text-sm font-semibold w-6 text-center">
                        {i.quantity}
                      </span>
                      <button
                        onClick={() => setQuantity(i.lookup_key, i.quantity + 1)}
                        data-testid={`cart-plus-${i.product.slug}`}
                        className="w-7 h-7 rounded-full bg-[#F4FAFF] hover:bg-[#E0F2FE] grid place-items-center text-[#051937]"
                      >
                        <Plus size={12} />
                      </button>
                      <button
                        onClick={() => removeItem(i.lookup_key)}
                        data-testid={`cart-remove-${i.product.slug}`}
                        className="ml-auto w-7 h-7 rounded-full hover:bg-[#FECACA] grid place-items-center text-[#DC2626]"
                        title="Usuń"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="font-display font-semibold text-[#051937] whitespace-nowrap">
                    {i.product.price_pln * i.quantity} zł
                  </div>
                </div>
              ))}
            </div>

            {detailed.length > 0 && (
              <div className="p-6 border-t border-[#BAE6FD] space-y-4 bg-[#F4FAFF]/60">
                <div className="flex items-center justify-between">
                  <span className="text-sm uppercase tracking-[0.16em] font-bold text-[#0b2a55]/70">
                    Razem
                  </span>
                  <span
                    className="font-display font-semibold text-[#051937] text-2xl"
                    data-testid="cart-total"
                  >
                    {total} zł
                  </span>
                </div>

                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e-mail (opcjonalnie)"
                  className="w-full rounded-full bg-white border border-[#BAE6FD] px-5 py-3 text-[#051937] placeholder:text-[#0b2a55]/40 text-sm outline-none focus:border-[#60A5FA]"
                  data-testid="cart-email"
                />

                <button
                  onClick={onCheckout}
                  disabled={loading}
                  data-testid="cart-checkout"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#051937] hover:bg-[#0b2a55] disabled:opacity-70 text-white px-6 py-4 font-semibold"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <ShoppingBag size={18} />}
                  {loading ? "Otwieram płatność…" : "Zapłać bezpiecznie"}
                </button>
                <button
                  onClick={clear}
                  className="w-full text-sm text-[#0b2a55]/70 hover:text-[#051937] font-semibold"
                  data-testid="cart-clear"
                >
                  Wyczyść koszyk
                </button>
                <p className="text-[10px] uppercase tracking-[0.14em] text-[#0b2a55]/50 text-center font-bold">
                  Płatność zabezpieczona przez Stripe · karta 4242 4242 4242 4242 w trybie testowym
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
